/*
 * Solutions pages: the small client behaviours of the Next.js solution components.
 *
 *   [data-orbit]            ProgrammeOrbit: hover / focus / click a node to show its text
 *   [data-accordion]        CareSection: one open item at a time
 *   [data-tracker]          CareSection: service request tracker walking through its stages
 *   [data-enquiry-prefill]  Moments: scroll to the enquiry form and tick a "help with" option
 *   [data-tilt]             InteractiveCoffeeImage: the coffee bag follows the mouse
 *   [data-pick-add]         SolutionCoffees: the bag button turns into a tick after adding
 *
 * Loaded by each section that needs it (guarded, so several script tags run it once) and re-run
 * for a section the theme editor reloads.
 */
(() => {
  "use strict";
  if (window.__championsSolutions) return;
  window.__championsSolutions = true;

  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduce = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const swap = (el, on, onCls, offCls) => {
    onCls.split(" ").forEach((c) => el.classList.toggle(c, on));
    offCls.split(" ").forEach((c) => el.classList.toggle(c, !on));
  };
  const replay = (el) => {
    if (!el) return;
    el.classList.remove("rise-in");
    void el.offsetWidth;
    el.classList.add("rise-in");
  };

  /* ---------------------------------------------------------------- programme orbit */
  const ORBIT_ON = "border-navy bg-navy text-[#f6f1e7]";
  const ORBIT_OFF = "border-ink/15 bg-[#fbf7ef] text-ink/80 hover:border-gold/60";
  function initOrbit(root) {
    $$("[data-orbit]", root).forEach((orbit) => {
      if (orbit.__orbit) return;
      orbit.__orbit = true;
      const nodes = $$("[data-orbit-node]", orbit);
      const lines = $$("[data-orbit-line]", orbit);
      const count = orbit.querySelector("[data-orbit-count]");
      const title = orbit.querySelector("[data-orbit-title]");
      const desc = orbit.querySelector("[data-orbit-desc]");
      const total = String(nodes.length).padStart(2, "0");
      let active = 0;
      const set = (i) => {
        if (i === active) return;
        active = i;
        nodes.forEach((n, j) => {
          n.setAttribute("aria-pressed", String(j === i));
          const pill = n.querySelector("span");
          pill && swap(pill, j === i, ORBIT_ON, ORBIT_OFF);
        });
        lines.forEach((l, j) => l.setAttribute("stroke", j === i ? "rgba(198,166,100,0.8)" : "rgba(239,232,220,0.08)"));
        if (count) count.textContent = `${String(i + 1).padStart(2, "0")} / ${total}`;
        if (title) {
          title.textContent = nodes[i].dataset.title || "";
          replay(title);
        }
        if (desc) {
          desc.textContent = nodes[i].dataset.desc || "";
          replay(desc);
        }
      };
      nodes.forEach((n, i) => {
        n.addEventListener("mouseenter", () => set(i));
        n.addEventListener("focus", () => set(i));
        n.addEventListener("click", () => set(i));
      });
      // Nodes pop in once the orbit is on screen (the spring in the React version).
      if (!reduce() && "IntersectionObserver" in window) {
        nodes.forEach((n) => n.classList.add("opacity-0", "scale-[0.4]"));
        const io = new IntersectionObserver(
          (entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            io.disconnect();
            nodes.forEach((n, i) => {
              n.style.transitionDelay = `${Math.min(i * 0.03, 0.15)}s`;
              n.classList.remove("opacity-0", "scale-[0.4]");
            });
          },
          { rootMargin: "-15% 0px" },
        );
        io.observe(orbit);
      }
    });
  }

  /* ---------------------------------------------------------------- care accordion */
  function initAccordion(root) {
    $$("[data-accordion]", root).forEach((acc) => {
      if (acc.__acc) return;
      acc.__acc = true;
      const toggles = $$("[data-accordion-toggle]", acc);
      const paint = (open) =>
        toggles.forEach((t) => {
          const on = t === open;
          t.setAttribute("aria-expanded", String(on));
          const panel = document.getElementById(t.getAttribute("aria-controls"));
          if (panel) {
            panel.hidden = !on;
            if (on) replay(panel);
          }
          const label = t.querySelector("[data-accordion-label]");
          label && label.classList.toggle("text-gold-soft", on);
          const icon = t.querySelector("svg");
          icon && icon.classList.toggle("rotate-45", on);
        });
      toggles.forEach((t) => t.addEventListener("click", () => paint(t.getAttribute("aria-expanded") === "true" ? null : t)));
    });
  }

  /* ---------------------------------------------------------------- service tracker */
  const NODE_ON = "border-gold scope-dark bg-[#15110d]/85 text-ivory shadow-[0_0_40px_-6px_rgba(198,166,100,0.6)]";
  const NODE_OFF = "border-ivory/20 scope-dark bg-[#15110d]/60 text-ivory/45";
  function initTracker(root) {
    $$("[data-tracker]", root).forEach((tracker) => {
      if (tracker.__tracker) return;
      tracker.__tracker = true;
      const nodes = $$("[data-tracker-node]", tracker);
      const path = tracker.querySelector("[data-tracker-path]");
      const paint = (step) =>
        nodes.forEach((n, i) => {
          const on = step >= i;
          swap(n, on, NODE_ON, NODE_OFF);
          const icon = n.querySelector("svg");
          icon && icon.classList.toggle("text-gold", on);
        });
      let timer = 0;
      let i = -1;
      const start = () => {
        if (path) path.style.strokeDashoffset = "0";
        if (reduce()) return paint(2);
        if (timer) return;
        timer = window.setInterval(() => {
          i = i >= nodes.length ? -1 : i + 1;
          paint(Math.min(i, nodes.length - 1));
        }, 1300);
      };
      const stop = () => {
        window.clearInterval(timer);
        timer = 0;
      };
      if (!("IntersectionObserver" in window)) return paint(nodes.length - 1);
      // The arc draws itself in when the tracker comes into view (needs pathLength="1").
      if (path && !reduce()) {
        path.style.strokeDasharray = "1";
        path.style.strokeDashoffset = "1";
        path.style.transition = "stroke-dashoffset 0.4s cubic-bezier(0.22, 1, 0.36, 1)";
      }
      new IntersectionObserver(([e]) => (e.isIntersecting ? start() : stop()), { rootMargin: "-20% 0px" }).observe(tracker);
    });
  }

  /* ---------------------------------------------------------------- enquiry prefill */
  function initPrefill() {
    if (document.__enquiryPrefill) return;
    document.__enquiryPrefill = true;
    document.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-enquiry-prefill]");
      if (!btn) return;
      const target = document.getElementById(btn.dataset.enquiryTarget || "plan-your-space");
      if (!target) return;
      e.preventDefault();
      const help = btn.dataset.enquiryPrefill;
      if (help) {
        const box = $$('input[type="checkbox"][data-help-option]', target).find((c) => c.value === help);
        if (box && !box.checked) {
          box.checked = true;
          box.dispatchEvent(new Event("input", { bubbles: true }));
          box.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
      target.scrollIntoView({ behavior: reduce() ? "auto" : "smooth" });
    });
  }

  /* ---------------------------------------------------------------- bag tilt */
  const MAX_TILT = 23.52;
  function initTilt(root) {
    $$("[data-tilt]", root).forEach((box) => {
      if (box.__tilt) return;
      box.__tilt = true;
      const target = box.querySelector("[data-tilt-target]");
      if (!target) return;
      const reset = () => (target.style.transform = "rotateX(0deg) rotateY(0deg)");
      box.addEventListener("pointermove", (e) => {
        if (reduce() || e.pointerType !== "mouse") return;
        const r = box.getBoundingClientRect();
        if (!r.width || !r.height) return;
        const x = Math.max(-1, Math.min(1, ((e.clientX - r.left) / r.width) * 2 - 1));
        const y = Math.max(-1, Math.min(1, ((e.clientY - r.top) / r.height) * 2 - 1));
        const m = Math.max(1, Math.hypot(x, y));
        target.style.transform = `rotateX(${((-y / m) * MAX_TILT).toFixed(2)}deg) rotateY(${((x / m) * MAX_TILT).toFixed(2)}deg)`;
      });
      box.addEventListener("pointerleave", reset);
      box.addEventListener("pointercancel", reset);
    });
  }

  /* ---------------------------------------------------------------- add-to-cart tick */
  function initPickAdd() {
    if (document.__pickAdd) return;
    document.__pickAdd = true;
    let pending = null;
    document.addEventListener("click", (e) => {
      const b = e.target.closest("[data-pick-add]");
      if (b) pending = b;
    });
    document.addEventListener("champions:cart-updated", () => {
      const b = pending;
      pending = null;
      if (!b) return;
      const bag = b.querySelector("[data-icon-bag]");
      const tick = b.querySelector("[data-icon-check]");
      if (!bag || !tick) return;
      bag.classList.add("hidden");
      tick.classList.remove("hidden");
      window.setTimeout(() => {
        bag.classList.remove("hidden");
        tick.classList.add("hidden");
      }, 1800);
    });
  }

  function init(root = document) {
    initOrbit(root);
    initAccordion(root);
    initTracker(root);
    initTilt(root);
    initPrefill();
    initPickAdd();
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => init());
  else init();
  document.addEventListener("shopify:section:load", (e) => init(e.target));
})();
